/*
  HappyLunch: movimiento de marca. Sin dependencias.
  - Títulos que entran palabra por palabra ([data-hl-split]).
  - Trazos a mano: <em> en un título = subrayado, <strong> = marcador.
  - Botón de pausa de las cintas en movimiento.
  - Barra fija de compra en la ficha de producto (celular).
  Todo respeta "reducir movimiento" y los ajustes de Marca HappyLunch.
*/
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const motion = root.classList.contains('hl-motion') && !reduceMotion;

  // ---------- Títulos palabra por palabra ----------
  function splitWords(element) {
    if (!motion || element.dataset.hlSplitDone) return;
    element.setAttribute('aria-label', element.textContent.replace(/\s+/g, ' ').trim());
    let index = 0;
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const fragment = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              fragment.appendChild(document.createTextNode(part));
              return;
            }
            const word = document.createElement('span');
            word.className = 'hl-word';
            word.setAttribute('aria-hidden', 'true');
            const inner = document.createElement('span');
            inner.textContent = part;
            inner.style.setProperty('--i', index++);
            word.appendChild(inner);
            fragment.appendChild(word);
          });
          child.replaceWith(fragment);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          walk(child);
        }
      });
    };
    walk(element);
    element.dataset.hlSplitDone = 'true';
    element.classList.add('hl-split');
  }

  // ---------- Trazos a mano ----------
  const doodleSelector = ':is(h1, h2, h3, .h0, .h1, .h2, .title, .hero-happy__heading)';
  let doodleObserver;

  function initDoodles(scope) {
    if (!root.classList.contains('hl-doodles')) return;
    const marks = scope.querySelectorAll(`${doodleSelector} em, ${doodleSelector} strong`);
    if (!marks.length) return;

    if (motion && !doodleObserver) {
      doodleObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-drawn');
            doodleObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -12% 0px' }
      );
    }

    marks.forEach((mark) => {
      if (mark.classList.contains('hl-doodle') || mark.classList.contains('hl-mark')) return;
      mark.classList.add(mark.tagName === 'EM' ? 'hl-doodle' : 'hl-mark');
      if (motion) {
        doodleObserver.observe(mark);
      } else {
        mark.classList.add('is-drawn');
      }
    });
  }

  // ---------- Pausa de las cintas ----------
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-hl-marquee-toggle]');
    if (!toggle) return;
    const marquee = toggle.closest('.hl-marquee');
    const paused = marquee.classList.toggle('is-paused');
    toggle.setAttribute('aria-pressed', String(paused));
    toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
  });

  function init(scope = document) {
    scope.querySelectorAll('[data-hl-split]').forEach(splitWords);
    initDoodles(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // En el personalizador, al editar una sección se vuelve a aplicar.
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  // ---------- Barra fija de compra (celular) ----------
  if (!customElements.get('hl-sticky-atc')) {
    customElements.define(
      'hl-sticky-atc',
      class HlStickyAtc extends HTMLElement {
        connectedCallback() {
          this.mainButton = document.querySelector('product-info .product-form__submit');
          this.button = this.querySelector('[data-hl-sticky-button]');
          this.label = this.querySelector('[data-hl-sticky-label]');
          this.price = this.querySelector('[data-hl-sticky-price]');
          if (!this.mainButton || !this.button) return;

          this.inert = true;
          this.button.addEventListener('click', () => this.mainButton.click());

          this.observer = new IntersectionObserver(([entry]) => {
            const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.classList.toggle('is-visible', show);
            this.inert = !show;
            document.body.classList.toggle('hl-sticky-atc-visible', show);
          });
          this.observer.observe(this.mainButton);

          this.sync = this.sync.bind(this);
          this.mutations = new MutationObserver(this.sync);
          this.mutations.observe(this.mainButton, { attributes: true, childList: true, subtree: true });
          this.priceSource = document.querySelector('product-info [id^="price-"]');
          if (this.priceSource) {
            this.mutations.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
          }
          this.sync();
        }

        sync() {
          this.button.disabled = this.mainButton.disabled;
          const text = this.mainButton.querySelector('span')?.textContent.trim();
          if (text && this.label) this.label.textContent = text;
          if (this.priceSource && this.price) {
            const current =
              this.priceSource.querySelector('.price--on-sale .price-item--last') ||
              this.priceSource.querySelector('.price__regular .price-item--regular');
            if (current) this.price.textContent = current.textContent.trim();
          }
        }

        disconnectedCallback() {
          this.observer?.disconnect();
          this.mutations?.disconnect();
          document.body.classList.remove('hl-sticky-atc-visible');
        }
      }
    );
  }
})();
